/**
 * Prospection — REST access to /compuzz/rest/v2/prospection.
 *
 * The board asks for one page at a time (the total travels with it), the
 * summary strip has its own call, and every change on a prospect (a call
 * logged, an owner, a stage, a next action) is one small request that
 * returns the updated row, so a screen never has to refetch the page to
 * show what it just did.
 */
define('entities/prospection',[
  'app',
  'jquery',
  'underscore',
  'settings'
], function (App, $, _, Settings) {
  'use strict';

  var API = {
    listUrl: function (params) { return Settings.url('compuzz2', 'v2/prospection', params); },
    summaryUrl: function () { return Settings.url('compuzz2', 'v2/prospection/summary'); },
    activityStatsUrl: function (params) { return Settings.url('compuzz2', 'v2/prospection/activity-stats', params); },
    activityFeedUrl: function (params) { return Settings.url('compuzz2', 'v2/prospection/activity-feed', params); },
    candidatesUrl: function (params) { return Settings.url('compuzz2', 'v2/prospection/candidates', params); },
    enrollUrl: function () { return Settings.url('compuzz2', 'v2/prospection/enroll'); },
    syncUrl: function () { return Settings.url('compuzz2', 'v2/prospection/sync'); },
    importUrl: function () { return Settings.url('compuzz2', 'v2/prospection/import'); },
    importTemplateUrl: function () { return Settings.url('compuzz2', 'v2/prospection/import/template'); },
    detailUrl: function (secId) { return Settings.url('compuzz2', 'v2/prospection/' + secId); },
    activityUrl: function (secId) { return Settings.url('compuzz2', 'v2/prospection/' + secId + '/activity'); },
    assignUrl: function (secId) { return Settings.url('compuzz2', 'v2/prospection/' + secId + '/assign'); },
    stageUrl: function (secId) { return Settings.url('compuzz2', 'v2/prospection/' + secId + '/stage'); },
    nextActionUrl: function (secId) { return Settings.url('compuzz2', 'v2/prospection/' + secId + '/next-action'); },
    touchUrl: function (secId) { return Settings.url('compuzz2', 'v2/prospection/' + secId + '/touch'); },
    refreshUrl: function (secId) { return Settings.url('compuzz2', 'v2/prospection/' + secId + '/refresh'); },
    removeUrl: function (secId) { return Settings.url('compuzz2', 'v2/prospection/' + secId); },
    byCustomerUrl: function (endUserSecId) { return Settings.url('compuzz2', 'v2/prospection/by-customer/' + endUserSecId); }
  };

  function get(url) {
    var defer = $.Deferred();
    $.ajax({
      type: 'GET',
      url: url,
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  }

  function send(type, url, body) {
    var defer = $.Deferred();
    $.ajax({
      type: type,
      url: url,
      contentType: 'application/json',
      data: body === undefined ? undefined : JSON.stringify(body || {}),
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  }

  // One page of the board: {total, first, pageSize, items}.
  App.reqres.setHandler('prospection:list', function (params) {
    return get(API.listUrl(params || {})).then(function (data) {
      return {
        total: (data && data.total) || 0,
        items: (data && data.items) || []
      };
    });
  });

  // Activity per user over a period ({from, to} epoch millis of the first / last day).
  App.reqres.setHandler('prospection:activity-stats', function (params) {
    return get(API.activityStatsUrl(params || {}));
  });

  // The journal across prospects: {total, first, pageSize, items}.
  App.reqres.setHandler('prospection:activity-feed', function (params) {
    return get(API.activityFeedUrl(params || {}));
  });

  App.reqres.setHandler('prospection:summary', function () {
    return get(API.summaryUrl());
  });

  // Customers / leads matching a search, each flagged with prospectionSecId
  // when it is already on the board.
  App.reqres.setHandler('prospection:candidates', function (search) {
    return get(API.candidatesUrl({ search: search, pageSize: 20 }));
  });

  // {endUserSecIds: [...]} or {allLeads: true} -> {created, skipped, remainingLeads, items}
  App.reqres.setHandler('prospection:enroll', function (body) {
    return send('POST', API.enrollUrl(), body);
  });

  // Enrol several customers from any list (orders, customers, leads, a company's
  // sub-accounts): body carries endUserSecIds and/or endUserIds (business codes).
  // Tells the user how many were added / already there, and lets an open board
  // know about the new rows. Resolves with the server's summary.
  App.reqres.setHandler('prospection:enroll-many', function (body) {
    var defer = $.Deferred();
    var wanted = ((body && body.endUserSecIds) || []).length + ((body && body.endUserIds) || []).length;
    if (!wanted) {
      require(['modalsAlertView'], function (AlertView) {
        AlertView.prototype.open('', _.i18n('prospection.enroll.none', null, 'Select at least one customer first.'));
      });
      return defer.reject().promise();
    }
    send('POST', API.enrollUrl(), body).done(function (result) {
      var created = (result && result.created) || 0;
      var skipped = (result && result.skipped) || 0;
      _.each((result && result.items) || [], function (item) {
        App.trigger('prospection:enrolled', item);
      });
      require(['modalsAlertView'], function (AlertView) {
        var text = created + ' ' + _.i18n('prospection.enroll.added', null, 'added to the prospection board');
        if (skipped) {
          text += ', ' + skipped + ' ' + _.i18n('prospection.enroll.skipped', null, 'already there (or not found)');
        }
        AlertView.prototype.open('', text + '.', null, created ? 'success' : 'warning');
      });
      defer.resolve(result);
    }).fail(function (xhr) {
      require(['modalsAlertView'], function (AlertView) {
        AlertView.prototype.open('', _.i18n('prospection.enroll.failed', null, 'Could not add to the prospection board.'));
      });
      defer.reject(xhr);
    });
    return defer.promise();
  });

  // An Excel / CSV file of prospects: each line is matched to a customer or lead
  // (code, then email) or created as a lead, then put on the board.
  // -> {lines, matched, leadsCreated, enrolled, alreadyOnBoard, duplicates,
  //     errors: [{line, label, message}], warnings: [...], items}
  App.reqres.setHandler('prospection:import', function (file) {
    var defer = $.Deferred();
    var formData = new FormData();
    formData.append('file', file, file.name);
    $.ajax({
      type: 'POST',
      url: API.importUrl(),
      data: formData,
      processData: false,
      contentType: false,
      success: function (data) { defer.resolve(data); },
      error: function (xhr) { defer.reject(xhr); }
    });
    return defer.promise();
  });

  App.reqres.setHandler('prospection:import-template-url', function () {
    return API.importTemplateUrl();
  });

  App.reqres.setHandler('prospection:sync', function () {
    return send('POST', API.syncUrl(), {});
  });

  App.reqres.setHandler('prospection:detail', function (secId) {
    return get(API.detailUrl(secId));
  });

  // {type, outcome, note, stage, nextActionAt, nextActionType, nextActionNote, clearNextAction, claim}
  App.reqres.setHandler('prospection:activity', function (secId, body) {
    return send('POST', API.activityUrl(secId), body);
  });

  // userpidSecId: a user's secId, 'me', or null to release.
  App.reqres.setHandler('prospection:assign', function (secId, userpidSecId) {
    return send('PUT', API.assignUrl(secId), { userpidSecId: userpidSecId || null });
  });

  App.reqres.setHandler('prospection:stage', function (secId, stage, lostReason, note) {
    return send('PUT', API.stageUrl(secId), { stage: stage, lostReason: lostReason || null, note: note || null });
  });

  App.reqres.setHandler('prospection:next-action', function (secId, body) {
    return send('PUT', API.nextActionUrl(secId), body || { clearNextAction: true });
  });

  App.reqres.setHandler('prospection:touch', function (secId) {
    return send('POST', API.touchUrl(secId), {});
  });

  App.reqres.setHandler('prospection:refresh', function (secId) {
    return send('POST', API.refreshUrl(secId), {});
  });

  // The file of one customer / lead; resolved with null when it is not on the board.
  App.reqres.setHandler('prospection:by-customer', function (endUserSecId) {
    var defer = $.Deferred();
    $.ajax({
      type: 'GET',
      url: API.byCustomerUrl(endUserSecId),
      success: function (data) { defer.resolve(data || null); },
      error: function (xhr) {
        if (xhr && xhr.status === 404) { defer.resolve(null); } else { defer.reject(xhr); }
      }
    });
    return defer.promise();
  });

  App.reqres.setHandler('prospection:remove', function (secId) {
    return send('DELETE', API.removeUrl(secId));
  });

  return API;
});

